import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Heart, TrendingDown, TrendingUp, Minus, Calendar, BarChart3, Lock, FileText } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { headachesAPI, tensionsAPI } from '../services/api';
import { HeadacheStats, TensionStats } from '../types';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { useSubscription } from '../context/SubscriptionContext';
import { UpgradePrompt } from '../components/subscription/UpgradePrompt';
import { MedicalReportModal } from '../components/reports/MedicalReportModal';

type TabType = 'headaches' | 'tension';

const intensityColors = {
  leve: '#9CAF88',
  moderada: '#FBBF24',
  fuerte: '#E8927C',
};

export const StatsPage: React.FC = () => {
  const { canUse } = useSubscription();
  const [activeTab, setActiveTab] = useState<TabType>('headaches');
  const [headacheStats, setHeadacheStats] = useState<HeadacheStats | null>(null);
  const [tensionStats, setTensionStats] = useState<TensionStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const [headachesRes, tensionsRes] = await Promise.all([
        headachesAPI.getStats(),
        canUse('tensions') ? tensionsAPI.getStats() : Promise.resolve({ data: { stats: null } }),
      ]);
      setHeadacheStats(headachesRes.data.stats || null);
      setTensionStats(tensionsRes.data.stats || null);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable' | undefined) => {
    if (trend === 'down') return <TrendingDown size={16} className="text-green-500" />;
    if (trend === 'up') return <TrendingUp size={16} className="text-red-500" />;
    return <Minus size={16} className="text-gray-400" />;
  };

  const getTrendLabel = (trend: 'up' | 'down' | 'stable' | undefined) => {
    if (trend === 'down') return 'Mejorando';
    if (trend === 'up') return 'Aumentando';
    return 'Estable';
  };

  // Mock chart data based on stats
  const getMonthlyChartData = () => {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun'];
    const current = headacheStats?.thisMonth || 0;
    const last = headacheStats?.lastMonth || 0;

    // Generate a simple trend
    return months.map((month, index) => ({
      name: month,
      value: Math.max(0, Math.floor(last + (current - last) * (index / 5) + Math.random() * 3)),
    }));
  };

  const getIntensityData = () => {
    const breakdown = headacheStats?.intensityBreakdown || { leve: 0, moderada: 0, fuerte: 0 };
    return [
      { name: 'Leve', value: breakdown.leve || 0, color: intensityColors.leve },
      { name: 'Moderada', value: breakdown.moderada || 0, color: intensityColors.moderada },
      { name: 'Fuerte', value: breakdown.fuerte || 0, color: intensityColors.fuerte },
    ];
  };

  return (
    <div>
      <Header title="Estadísticas" />

      <main className="px-4 sm:px-6 pb-24 lg:pb-6 max-w-6xl mx-auto">
        {/* Tab Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-2 p-1.5 bg-[var(--color-surface)] rounded-2xl mb-6 max-w-md mx-auto lg:mx-0"
        >
          <button
            onClick={() => setActiveTab('headaches')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'headaches'
                ? 'bg-[var(--color-bg)] text-[var(--color-text)] shadow-sm'
                : 'text-[var(--color-text-muted)]'
            }`}
          >
            <Brain size={18} />
            Dolores
          </button>
          <button
            onClick={() => setActiveTab('tension')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'tension'
                ? 'bg-[var(--color-bg)] text-[var(--color-text)] shadow-sm'
                : 'text-[var(--color-text-muted)]'
            }`}
          >
            <Heart size={18} />
            Tensión
            {!canUse('tensions') && <Lock size={12} className="ml-1 opacity-60" />}
          </button>
        </motion.div>

        {/* Boton Informe Medico */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex justify-center mb-6"
        >
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--color-surface)] text-[var(--color-text)] font-medium text-sm hover:bg-[var(--color-bg)] transition-colors border border-[var(--color-border)]"
          >
            <FileText size={18} className="text-[var(--color-accent)]" />
            Generar informe médico
          </button>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'tension' && !canUse('tensions') ? (
          <UpgradePrompt feature="tensions" description="Las estadisticas de tension arterial estan disponibles en el plan Premium." />
        ) : activeTab === 'headaches' ? (
          /* Headache Stats */
          <motion.div
            key="headaches"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Summary Cards - 2 cols on mobile, 4 cols on desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Calendar size={18} className="text-[var(--color-accent)]" />
                  <span className="text-sm text-[var(--color-text-muted)]">Este mes</span>
                </div>
                <p className="text-3xl font-display font-bold text-[var(--color-text)]">
                  {headacheStats?.thisMonth || 0}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  {getTrendIcon(headacheStats?.trend)}
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {getTrendLabel(headacheStats?.trend)}
                  </span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="card p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 size={18} className="text-[var(--color-text-muted)]" />
                  <span className="text-sm text-[var(--color-text-muted)]">Mes pasado</span>
                </div>
                <p className="text-3xl font-display font-bold text-[var(--color-text)]">
                  {headacheStats?.lastMonth || 0}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {headacheStats?.thisMonth !== undefined && headacheStats?.lastMonth !== undefined
                      ? (headacheStats.thisMonth > headacheStats.lastMonth ? '+' : '') +
                        (headacheStats.thisMonth - headacheStats.lastMonth) + ' vs actual'
                      : '-'}
                  </span>
                </div>
              </motion.div>

              {/* Total Stats - visible on desktop in the same row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card p-5 col-span-2 lg:col-span-2"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)]">Total registrado</p>
                    <p className="text-2xl lg:text-3xl font-display font-bold text-[var(--color-text)]">
                      {headacheStats?.total || 0} episodios
                    </p>
                  </div>
                  <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--color-accent-soft)' }}>
                    <Brain size={28} style={{ color: 'var(--color-accent)' }} />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Charts Grid - side by side on desktop */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Trend Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="card p-5"
              >
                <h3 className="text-lg font-display font-semibold text-[var(--color-text)] mb-4">
                  Tendencia mensual
                </h3>
                <div className="h-48 lg:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={getMonthlyChartData()}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#E8927C" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#E8927C" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#E8927C"
                        strokeWidth={3}
                        fill="url(#colorValue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Intensity Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card p-5"
              >
                <h3 className="text-lg font-display font-semibold text-[var(--color-text)] mb-4">
                  Por intensidad
                </h3>
                <div className="h-40 lg:h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getIntensityData()} layout="vertical">
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--color-text)', fontSize: 14 }}
                        width={80}
                      />
                      <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                        {getIntensityData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="flex justify-center gap-6 mt-4">
                  {getIntensityData().map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ background: item.color }}
                      />
                      <span className="text-sm text-[var(--color-text-muted)]">
                        {item.name}: {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        ) : (
          /* Tension Stats */
          <motion.div
            key="tension"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Average Values - 2 cols on mobile, 4 cols on desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card p-5"
              >
                <p className="text-sm text-[var(--color-text-muted)] mb-2">Sistólica media</p>
                <p className="text-3xl font-display font-bold text-[var(--color-text)]">
                  {tensionStats?.avgSystolic ? Math.round(tensionStats.avgSystolic) : '-'}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">mmHg</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="card p-5"
              >
                <p className="text-sm text-[var(--color-text-muted)] mb-2">Diastólica media</p>
                <p className="text-3xl font-display font-bold text-[var(--color-text)]">
                  {tensionStats?.avgDiastolic ? Math.round(tensionStats.avgDiastolic) : '-'}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">mmHg</p>
              </motion.div>

              {/* Pulse Average */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card p-5"
              >
                <p className="text-sm text-[var(--color-text-muted)] mb-2">Pulso medio</p>
                <p className="text-3xl font-display font-bold text-[var(--color-text)]">
                  {tensionStats?.avgPulse ? Math.round(tensionStats.avgPulse) : '-'}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">bpm</p>
              </motion.div>

              {/* Total Count */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="card p-5"
              >
                <p className="text-sm text-[var(--color-text-muted)] mb-2">Total mediciones</p>
                <p className="text-3xl font-display font-bold text-[var(--color-text)]">
                  {tensionStats?.total || 0}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">registros</p>
              </motion.div>
            </div>

            {/* Ranges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card p-5"
            >
              <h3 className="text-lg font-display font-semibold text-[var(--color-text)] mb-4">
                Rangos registrados
              </h3>

              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[var(--color-text-muted)]">Sistólica</span>
                      <span className="text-[var(--color-text)]">
                        {tensionStats?.minSystolic || '-'} - {tensionStats?.maxSystolic || '-'} mmHg
                      </span>
                    </div>
                    <div className="h-3 bg-[var(--color-bg)] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: tensionStats?.avgSystolic ? '60%' : '0%',
                          background: 'linear-gradient(90deg, #9CAF88 0%, #E8927C 100%)'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[var(--color-text-muted)]">Diastólica</span>
                      <span className="text-[var(--color-text)]">
                        {tensionStats?.minDiastolic || '-'} - {tensionStats?.maxDiastolic || '-'} mmHg
                      </span>
                    </div>
                    <div className="h-3 bg-[var(--color-bg)] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: tensionStats?.avgDiastolic ? '50%' : '0%',
                          background: 'linear-gradient(90deg, #9CAF88 0%, #FBBF24 100%)'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Blood Pressure Categories Info */}
                <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                  <h4 className="text-sm font-medium text-[var(--color-text)] mb-3">Categorías de tensión</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#22c55e]" />
                      <span className="text-[var(--color-text-muted)]">Normal: &lt;120/80</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#f59e0b]" />
                      <span className="text-[var(--color-text-muted)]">Elevada: 120-129/&lt;80</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#f97316]" />
                      <span className="text-[var(--color-text-muted)]">Grado 1: 130-139/80-89</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#ef4444]" />
                      <span className="text-[var(--color-text-muted)]">Grado 2: ≥140/≥90</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Latest reading card */}
            {tensionStats?.latest && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="card p-5"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)]">Última medición</p>
                    <p className="text-2xl font-display font-bold text-[var(--color-text)]">
                      {tensionStats.latest.systolic}/{tensionStats.latest.diastolic} mmHg
                    </p>
                    {tensionStats.latest.pulse && (
                      <p className="text-sm text-[var(--color-text-muted)]">
                        Pulso: {tensionStats.latest.pulse} bpm
                      </p>
                    )}
                  </div>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#9CAF8820' }}>
                    <Heart size={32} style={{ color: '#9CAF88' }} />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Empty State */}
            {!tensionStats?.total && (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: '#9CAF8820' }}>
                  <Heart size={32} style={{ color: '#9CAF88' }} />
                </div>
                <p className="text-[var(--color-text)] font-medium mb-1">Sin datos de tensión</p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Comienza a registrar tu tensión arterial
                </p>
              </div>
            )}
          </motion.div>
        )}
      </main>

      <MedicalReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
      />
    </div>
  );
};
